import { useMemo, useState } from 'react';
import { $getNodeByKey } from 'lexical';

import { StringsToOptions } from 'src/compass/shared/util/StringsToOptions';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import { getParsedHTML } from 'src/compass/shared/util/getParsedHTML';
import { useApiContext } from 'src/compass/site/providers/ApiContextProvider/ApiContextProvider';

import './DataObjectRenderer.css';

import { FormLabel } from '@mui/material';

import { CasSecondaryButton } from 'src/compass/shared/components/CasButton/CasSecondaryButton';
import CasButton from 'src/compass/shared/components/CasButton/CasButton';

import CasSelect from '../../../CasSelect/CasSelect';
import CasPanel from '../../../CasPanel/CasPanel';
import { $isCasTableDataNode, IDataTableConfig } from './CasTableDataNode';
import { useRenderedDataObject } from './DataObjectCacheContext';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

interface Props {
  dataObject: IDataTableConfig['dataObject'];
  renderDataObjectUrl: string;
  nodeKey: string;
}

export enum CasTableNodeLayout {
  Basic = 'Basic',
  Current_Value = 'Current Value',
  Prelim_Settle = 'Prelim Settle',
  Billing = 'Billing',
}

const LAYOUT_OPTIONS = StringsToOptions([
  CasTableNodeLayout.Basic,
  CasTableNodeLayout.Current_Value,
  CasTableNodeLayout.Prelim_Settle,
  CasTableNodeLayout.Billing,
]);

export function DataObjectRenderer({ renderDataObjectUrl, dataObject, nodeKey }: Props) {
  const [editor] = useLexicalComposerContext();
  const [layout, setLayout] = useState(dataObject.layout ?? '');

  // Keyed on the request, so switching Layout re-renders through the same cache-first path rather
  // than needing its own fetch in the onChange below.
  const request = useMemo(() => ({ ...dataObject, layout }), [dataObject, layout]);
  const result = useRenderedDataObject(renderDataObjectUrl, request);

  // Download the PDF this table would be attached to the email as, so the sender can review it
  // before sending. Hits the sibling RenderPdf endpoint with this table's exact dataObject, so
  // what downloads is byte-for-byte what the recipient gets.
  const { api } = useApiContext();
  const [pdfPending, setPdfPending] = useState(false);
  const downloadPdf = async () => {
    setPdfPending(true);
    try {
      const url = renderDataObjectUrl.replace(/RenderDataObject$/, 'RenderPdf');
      const response = await api.post(url, { ...dataObject, layout }, { responseType: 'blob' });
      const href = URL.createObjectURL(response.data as Blob);
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = 'SCE Report.pdf';
      anchor.click();
      URL.revokeObjectURL(href);
    } finally {
      setPdfPending(false);
    }
  };

  // Drop this data object out of the editor content. Removing the node unmounts this renderer, so
  // there is nothing else to clean up here; the editor's undo puts it back.
  const removeDataObject = () => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isCasTableDataNode(node)) {
        node.remove();
      }
    });
  };

  return (
    <CasPanel
      variant="outlined"
      maxHeight="200px"
      className="cas-data-object"
      data-table-key={nodeKey}
    >
      {/*
        This row is a header, not a form field, so it should not spend a field's worth of height.
        Labelling the select here rather than through CasSelect drops both of the things that
        made it tall: the floating label's own line above the control, and the 12px bottom margin
        every MuiFormControl carries (Input.scss) - with label={null} CasControl renders the
        select bare, without that FormControl.
      */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FormLabel
            // Label.scss floats every label into its control with translate(12px, 11px); this
            // one sits beside the select instead, so it has to opt out. The &.MuiFormLabel-root
            // is what gives the override the specificity to win.
            sx={{ '&.MuiFormLabel-root': { transform: 'none' }, fontSize: 12, whiteSpace: 'nowrap' }}
          >
            Layout
          </FormLabel>
          <CasSelect
            label={null}
            value={layout}
            onChange={(_, value) => {
              // No fetch here: setLayout changes the cache key, and useRenderedDataObject
              // re-renders the table from the cache when this layout has already been seen.
              setLayout(value);
              editor.update(() => {
                const node = $getNodeByKey(nodeKey);
                if ($isCasTableDataNode(node)) {
                  const writable = node.getWritable();
                  writable.__dataObject = {
                    ...writable.__dataObject,
                    layout: value,
                  };
                }
              });
            }}
            options={LAYOUT_OPTIONS}
            // The select's default 16px top padding is room for the floating label it no longer
            // has, which left the control a full field tall in what is only a header row.
            sx={{
              '& .MuiSelect-select': {
                paddingTop: '4px',
                paddingBottom: '4px',
                fontSize: 12,
                minHeight: 'unset',
              },
            }}
          />
          {/* Sized like the header's other controls - a link, not a field-height button. */}
          <CasButton
            variant="text"
            size="small"
            onClick={() => removeDataObject()}
            sx={{
              fontSize: 12,
              // MuiButton's theme weight is a button's, not a link's - this row reads as text.
              fontWeight: 400,
              minWidth: 0,
              p: 0,
              whiteSpace: 'nowrap',
            }}
          >
            Remove
          </CasButton>
        </div>
        {result?.docUrl && (
          <a
            href={result.docUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '12px', whiteSpace: 'nowrap', textDecoration: 'none' }}
          >
            Column Definitions
          </a>
        )}
        <CasSecondaryButton
          size="small"
          onClick={() => downloadPdf()}
          disabled={pdfPending}
          sx={{ py: 0.125, px: 1, fontSize: 11, minWidth: 0, whiteSpace: 'nowrap', lineHeight: 1.6 }}
        >
          {pdfPending ? 'Generating…' : 'Review PDF'}
        </CasSecondaryButton>
      </div>
      {!result ? (
        <CasLoader />
      ) : (
        <div style={{ fontSize: '12px' }}>{getParsedHTML(result.html)}</div>
      )}
    </CasPanel>
  );
}
